import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Container,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useApiGet } from '../../../utils/httpClientTs';
import { getMyPermissions } from '../../../endpoints/usersTs';
import { hasUserApplicationPermission } from '../../../utils/permissions';
import {
  FOUNDATION_WAITING_LABELS,
  listFoundationApplications,
  type FoundationApplicationListItem,
} from '../../../endpoints/foundation';
import PageHeader from '../../../Components/PageHeader';
import ServerSideTable, {
  type ServerSideTableColumn,
} from '../../../Components/ServerSideTable';

const STAGE_OPTIONS = [
  { value: '', label: 'All stages' },
  { value: 'draft', label: 'Draft' },
  { value: 'discarded', label: 'Discarded' },
  { value: 'gate_trustee_hold', label: 'Trustee hold' },
  { value: 'parallel_review', label: 'Parallel review' },
  { value: 'post_award_docs', label: 'Post-award docs' },
  { value: 'final_io_approval', label: 'Final IO approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'cancelled', label: 'Cancelled' },
];

export default function FoundationApplicantsList() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('');
  const [academicYear, setAcademicYear] = useState('2025-2026');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data: permissions } = useApiGet<string[]>(
    ['my-permissions-foundation-applicants'],
    () => getMyPermissions({ international: '1' }),
    { enabled: true }
  );
  const canRecommend = hasUserApplicationPermission(
    permissions,
    'foundation:recommend'
  );

  const { data, isLoading, refetch } = useApiGet<{
    items: FoundationApplicationListItem[];
  }>(
    ['foundation-applications', stage, academicYear],
    () =>
      listFoundationApplications({
        stage: stage || undefined,
        academic_year: academicYear || undefined,
      }),
    { enabled: true, retry: 1, refetchOnMount: 'always' }
  );

  const rows: FoundationApplicationListItem[] = data?.items ?? [];
  const paged = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const columns: ServerSideTableColumn<FoundationApplicationListItem>[] =
    useMemo(
      () => [
        {
          id: 'student_last_name',
          label: 'Student',
          render: (_v, row) =>
            [row.student_first_name, row.student_last_name]
              .filter(Boolean)
              .join(' ') || '—',
        },
        { id: 'stage', label: 'Stage' },
        {
          id: 'waiting',
          label: 'Waiting',
          render: (_v, row) => {
            const keys = row.waiting || [];
            if (keys.length === 0) return '—';
            return (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {keys.map((key) => (
                  <Chip
                    key={key}
                    size="small"
                    color="warning"
                    label={FOUNDATION_WAITING_LABELS[key] || key}
                  />
                ))}
              </Stack>
            );
          },
        },
        { id: 'academic_year', label: 'Year' },
        {
          id: 'awarded_amount',
          label: 'Amount',
          render: (_v, row) =>
            `${row.currency || 'USD'} ${Number(row.awarded_amount || 0).toFixed(2)}`,
        },
        {
          id: 'updated_at',
          label: 'Updated',
          render: (_v, row) =>
            row.updated_at
              ? new Date(row.updated_at).toLocaleDateString()
              : '—',
        },
      ],
      []
    );

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <PageHeader
        title="Foundation Applicants"
        leftDom={undefined}
        rightDom={undefined}
        subtitle={undefined}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Recommendations and in-flight review cases
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        {canRecommend && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/foundation/applicants/new"
          >
            New ICH
          </Button>
        )}
        <TextField
          select
          size="small"
          label="Stage"
          value={stage}
          onChange={(e) => {
            setStage(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 200 }}
        >
          {STAGE_OPTIONS.map((o) => (
            <MenuItem key={o.value || 'all'} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Academic year"
          value={academicYear}
          onChange={(e) => {
            setAcademicYear(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 160 }}
        />
        <Button onClick={() => refetch()}>Refresh</Button>
      </Stack>
      <Box>
        <ServerSideTable
          data={paged}
          columns={columns}
          loading={!!isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={rows.length}
          totalPages={Math.max(1, Math.ceil(rows.length / rowsPerPage))}
          hasNext={(page + 1) * rowsPerPage < rows.length}
          hasPrev={page > 0}
          order="desc"
          orderBy="updated_at"
          onPageChange={setPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setPage(0);
          }}
          onSort={() => undefined}
          onRowClick={(row) => navigate(`/foundation/applicants/${row.id}`)}
          emptyMessage="No applications found."
        />
      </Box>
    </Container>
  );
}
